import React, { useEffect, useRef, useState } from 'react';
import { motion, useSpring, useTransform, useMotionValue } from 'framer-motion';
import './GWagonBackground.css';

/**
 * Site-wide interactive G-Wagon background.
 *
 * Renders a large low-opacity G-Wagon behind all page content that:
 *  - Tilts and shifts with mouse position (desktop) — subtle 3D pseudo-parallax
 *  - Drifts on scroll — the vehicle "moves" as the user scrolls the page
 *  - Reacts to device orientation on mobile if permission granted
 *  - Sits at z-index 0 with fixed positioning behind main content
 */
const GWagonBackground = () => {
  const wrapperRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);

  // Mouse-driven motion values with spring smoothing
  const mouseX = useMotionValue(0.5);
  const mouseY = useMotionValue(0.5);
  const springX = useSpring(mouseX, { stiffness: 60, damping: 20 });
  const springY = useSpring(mouseY, { stiffness: 60, damping: 20 });

  const rotateY = useTransform(springX, [0, 1], [-14, 14]);
  const rotateX = useTransform(springY, [0, 1], [8, -8]);
  const shiftX = useTransform(springX, [0, 1], [-40, 40]);
  const shiftY = useTransform(springY, [0, 1], [-24, 24]);

  useEffect(() => {
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    const onMouseMove = (e) => {
      mouseX.set(e.clientX / window.innerWidth);
      mouseY.set(e.clientY / window.innerHeight);
    };
    const onScroll = () => setScrollY(window.scrollY);
    const onOrientation = (e) => {
      if (typeof e.gamma !== 'number' || typeof e.beta !== 'number') return;
      // gamma: -90 to 90 (left/right), beta: -180 to 180 (front/back)
      mouseX.set(Math.max(0, Math.min(1, 0.5 + e.gamma / 60)));
      mouseY.set(Math.max(0, Math.min(1, 0.5 + (e.beta - 45) / 90)));
    };

    if (!isTouch) {
      window.addEventListener('mousemove', onMouseMove, { passive: true });
    } else if (window.DeviceOrientationEvent) {
      window.addEventListener('deviceorientation', onOrientation, { passive: true });
    }
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('deviceorientation', onOrientation);
      window.removeEventListener('scroll', onScroll);
    };
  }, [mouseX, mouseY]);

  // Scroll parallax — vehicle drifts horizontally as user scrolls
  const scrollDrift = Math.min(scrollY * 0.08, 260);

  return (
    <div
      className="gw-bg"
      ref={wrapperRef}
      aria-hidden="true"
      data-testid="gwagon-bg"
    >
      <div className="gw-bg-scene">
        <motion.div
          className="gw-bg-plate"
          style={{
            rotateY,
            rotateX,
            x: shiftX,
            y: shiftY,
          }}
        >
          <motion.img
            src="/founder/hero-bg.png"
            alt=""
            className="gw-bg-img"
            style={{ x: -scrollDrift }}
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.div>
      </div>
      <div className="gw-bg-veil" />
    </div>
  );
};

export default GWagonBackground;
